import '../styles/LessonCard.css';
import '../styles/LessonStatus.css';

import { Dialog, DialogContent } from '@mui/material';
import React, { useState } from 'react';

import { ApiPortfolioState } from '../models/ApiPortfolioState';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import { CourseLessonVm } from '../models/LessonVm';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import { LessonFlowWarningDialog } from './LessonFlowWarningDialog';
import ProgressStatusCode from '../models/ProgressStatusCode';
import Typography from '@mui/material/Typography';

export type NewAttemptLabel = 'Reattempt' | 'Restart';
export type OldAttemptLabel = 'Review' | 'Continue';

interface WarningType {
  open: boolean;
  label: NewAttemptLabel;
}

const contentTitle: Record<ProgressStatusCode, string> = {
  E: `You completed this lesson some time ago, and your certificate has expired.`,
  S: `You have previously started this lesson.`,
  N: '',
  F: `You have previously failed this lesson.`,
  L: '',
  P: `You have previously passed this lesson.`,
  C: 'You have previously completed this lesson.',
  X: 'You completed this lesson some time ago, and your certificate is going to expire soon.',
  M: '',
  R: `You have previously started this lesson.`
};

interface ILessonFlowDialog {
  loading: boolean;
  lesson: CourseLessonVm;
  handleContinue: () => void;
  handleRestart: () => void;
  handleReview: () => void;
  handleClose: () => void;
}

export const LessonFlowDialog = (props: ILessonFlowDialog) => {
  const {
    title,
    type,
    allowRestart,
    newVersionAvailable: newVersion,
    latestPortfolioAttempt
  } = props.lesson;
  const portfolioState = latestPortfolioAttempt?.state;
  const lessonProgressCode = latestPortfolioAttempt?.status;
  const { loading, handleClose, handleContinue, handleReview, handleRestart } =
    props;
  const [warning, setWarning] = useState<WarningType>();

  const progressCode: ProgressStatusCode =
    type === 'lesson' && lessonProgressCode === 'P' ? 'C' : lessonProgressCode;

  const expired = progressCode === 'E';
  const canMakeNewAttempt: boolean = allowRestart || (expired && newVersion);

  const portfolioLocked =
    expired ||
    [
      ApiPortfolioState.QuizLock,
      ApiPortfolioState.AssessmentLock,
      ApiPortfolioState.AssessmentSubmit,
      ApiPortfolioState.AssessmentMark
    ].includes(portfolioState);
  const newAttemptLabel: NewAttemptLabel = portfolioLocked
    ? 'Reattempt'
    : 'Restart';

  const oldAttemptLabel: OldAttemptLabel = portfolioLocked
    ? 'Review'
    : 'Continue';

  const handleRestartClick = (label: NewAttemptLabel) => {
    if (type === 'assessment' && portfolioLocked) {
      // B2-1561 Exceptional rule for `assessment` lessons:
      // When portfolio is locked,no need to show warning dialog.
      handleRestart();
    } else {
      setWarning({ label, open: true });
    }
  };

  return (
    <>
      <Dialog open={!warning?.open}>
        <DialogContent style={{ ...(loading && { height: 180, width: 500 }) }}>
          {loading ? (
            <CustomLoadingProgress />
          ) : (
            <Grid
              container
              spacing={2}
              justifyContent={'center'}
              direction={'column'}
            >
              <IconButton
                color="primary"
                size="small"
                onClick={handleClose}
                sx={{ alignSelf: 'end' }}
              >
                <CloseIcon />
              </IconButton>
              <Grid sx={{ alignSelf: 'center' }}>
                <Typography variant="body1" textAlign={'center'}>
                  {`Starting ${type}: ${title}`}
                </Typography>
                <Typography variant="h6" textAlign={'center'}>
                  {!portfolioLocked && newVersion
                    ? `The content of this lesson has been updated. You may see some changes.`
                    : contentTitle[progressCode]}
                </Typography>
              </Grid>

              <Grid>
                <Grid container spacing={2} justifyContent={'center'}>
                  {canMakeNewAttempt && (
                    <Grid>
                      <Button
                        color="primary"
                        variant="outlined"
                        size="medium"
                        onClick={() => handleRestartClick(newAttemptLabel)}
                      >
                        {newAttemptLabel}
                      </Button>
                    </Grid>
                  )}
                  <Grid>
                    <Button
                      color="primary"
                      variant="contained"
                      size="medium"
                      onClick={
                        oldAttemptLabel === 'Review'
                          ? handleReview
                          : handleContinue
                      }
                    >
                      {oldAttemptLabel}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              {canMakeNewAttempt &&
                (type !== 'assessment' ||
                  // B2-1561 Exceptional rule for `assessment` lessons:
                  // Don't show this paragraph for 'assessment' when the selected
                  // portfolio is locked.
                  (type === 'assessment' && !portfolioLocked)) && (
                  <Grid sx={{ textAlign: 'center' }}>
                    <Typography variant="body2">
                      Note: {newAttemptLabel}ing this lesson will erase your
                      progress, which cannot then be recovered.
                    </Typography>
                  </Grid>
                )}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
      {warning?.open && (
        <LessonFlowWarningDialog
          label={warning.label}
          handleClose={handleClose}
          handleConfirmRestart={handleRestart}
        />
      )}
    </>
  );
};
